import React, { useEffect, useState } from "react";
import { TrendingUp, Plus, FileText, CheckCircle2, Wand2, ArrowLeft } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader, Card, Badge, Loading, Modal, EmptyState, fmtMoney, fmtDate } from "../components/ui";

const STATUS_TONE = { DRAFT: "info", GENERATED: "warn", APPROVED: "ok" };

export default function Compensation() {
  const [cycles, setCycles] = useState(null);
  const [sel, setSel] = useState(null);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", effectiveDate: "", budgetPct: 9 });
  const [busy, setBusy] = useState(false);

  const loadCycles = () => api.get("/compensation/cycles").then(({ data }) => setCycles(data));
  const loadProposals = (cid) => api.get(`/compensation/cycles/${cid}/proposals`).then(({ data }) => setData(data));
  useEffect(() => { loadCycles(); }, []);
  useEffect(() => { if (sel) loadProposals(sel.id); }, [sel]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/compensation/cycles", form); setOpen(false); setForm({ name: "", effectiveDate: "", budgetPct: 9 }); loadCycles(); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const generate = async () => {
    if (sel.status === "GENERATED" && !window.confirm("Regenerate will discard any manual hike edits and rebuild proposals from ratings. Continue?")) return;
    setBusy(true); try { await api.post(`/compensation/cycles/${sel.id}/generate`); await loadProposals(sel.id); setSel({ ...sel, status: "GENERATED" }); loadCycles(); } catch (e) { window.alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const approve = async () => {
    if (!window.confirm("Approve & apply? This revises salary structures and generates increment letters for all employees with a hike.")) return;
    setBusy(true);
    try { const { data } = await api.post(`/compensation/cycles/${sel.id}/approve`); window.alert(`Applied ${data.applied} revisions. Increment letters generated & emailed (mock).`); setSel({ ...sel, status: "APPROVED" }); loadProposals(sel.id); loadCycles(); }
    catch (e) { window.alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const editHike = async (p, hikePct) => {
    if (+hikePct === p.hikePct) return;
    try { await api.put(`/compensation/proposals/${p.id}`, { hikePct: +hikePct }); loadProposals(sel.id); }
    catch (e) { window.alert(apiError(e.response?.data?.detail)); }
  };
  const openLetter = async (lid) => { const res = await api.get(`/documents/letter/${lid}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); };

  if (!cycles) return <Loading />;

  if (sel) {
    return (
      <div>
        <button onClick={() => { setSel(null); setData(null); }} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline" data-testid="comp-back"><ArrowLeft size={15} /> All cycles</button>
        <PageHeader title={sel.name} subtitle={`Effective ${fmtDate(sel.effectiveDate)} · budget ${sel.budgetPct}%`}>
          {sel.status !== "APPROVED" && <button data-testid="comp-generate" disabled={busy} onClick={generate} className="btn-outline"><Wand2 size={15} /> {sel.status === "DRAFT" ? "Generate Proposals" : "Regenerate"}</button>}
          {sel.status === "GENERATED" && <button data-testid="comp-approve" disabled={busy} onClick={approve} className="btn-accent"><CheckCircle2 size={16} /> Approve & Apply</button>}
          {sel.status === "APPROVED" && <Badge tone="ok">Applied</Badge>}
        </PageHeader>
        {!data ? <Loading /> : data.proposals.length === 0 ? <EmptyState icon={TrendingUp} title="No proposals yet" hint="Click 'Generate Proposals' to draft hikes from appraisal ratings." /> : (<>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[["Employees", data.proposals.length], ["Avg Hike", `${data.avgHikePct}%`], ["Current Cost", fmtMoney(data.totalCurrent)], ["Total Increase", fmtMoney(data.totalIncrease)]].map(([k, v]) => (
              <Card key={k} className="p-5"><p className="text-sm font-medium text-slate-500 dark:text-slate-400">{k}</p><p className="mt-2 font-head text-2xl font-extrabold text-slate-900 dark:text-white">{v}</p></Card>
            ))}
          </div>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full" data-testid="comp-proposals-table">
              <thead><tr><th className="th">Employee</th><th className="th">Last Rating</th><th className="th">Current CTC</th><th className="th">Hike %</th><th className="th">Revised CTC</th><th className="th">Letter</th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.proposals.map((p) => (
                  <tr key={p.id} data-testid={`comp-row-${p.id}`}>
                    <td className="td font-medium text-slate-800 dark:text-slate-100">{p.employeeName}</td>
                    <td className="td">{p.rating ? <Badge tone={p.rating >= 4 ? "ok" : "info"}>{p.rating}/5</Badge> : <span className="text-xs text-slate-400">—</span>}</td>
                    <td className="td font-mono text-sm">{fmtMoney(p.currentCtc)}</td>
                    <td className="td">
                      {sel.status === "GENERATED" ? (
                        <input type="number" step="0.5" min="0" data-testid={`comp-hike-${p.id}`} defaultValue={p.hikePct} onBlur={(e) => editHike(p, e.target.value)} className="input h-8 w-20 py-0 font-mono" />
                      ) : <span className="font-mono text-sm font-semibold text-emerald-600">{p.hikePct}%</span>}
                    </td>
                    <td className="td font-mono text-sm font-bold">{fmtMoney(p.newCtc)}</td>
                    <td className="td">{p.letterId ? <button data-testid={`comp-letter-${p.id}`} onClick={() => openLetter(p.letterId)} className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"><FileText size={13} /> Open</button> : <span className="text-xs text-slate-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>
        </>)}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Compensation & Increments" subtitle="Merit cycles driven by appraisal ratings">
        <button data-testid="comp-new-cycle" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> New Cycle</button>
      </PageHeader>
      {cycles.length === 0 ? <EmptyState icon={TrendingUp} title="No increment cycles" hint="Create a cycle to propose merit-based salary revisions." /> : (
        <div className="grid gap-3" data-testid="comp-cycles">
          {cycles.map((c) => (
            <Card key={c.id} className="flex flex-wrap items-center justify-between gap-3 p-5 transition-colors hover:border-accent/50" data-testid={`comp-cycle-${c.id}`}>
              <div>
                <p className="font-semibold text-slate-800 dark:text-slate-100">{c.name}</p>
                <p className="text-xs text-slate-400">Effective {fmtDate(c.effectiveDate)} · budget {c.budgetPct}% · {c.proposalCount} proposals</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>
                <button data-testid={`comp-open-${c.id}`} onClick={() => setSel(c)} className="btn-outline px-4 py-1.5 text-sm">Open</button>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="New Increment Cycle"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="cc-form" data-testid="cc-submit" disabled={busy} className="btn-accent">Create</button></>}>
        <form id="cc-form" onSubmit={create} className="space-y-4">
          <div><label className="label">Cycle Name *</label><input required data-testid="cc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g. FY26 Annual Increment" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Effective Date *</label><input required type="date" data-testid="cc-date" value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} className="input" /></div>
            <div><label className="label">Budget %</label><input type="number" step="0.5" data-testid="cc-budget" value={form.budgetPct} onChange={(e) => setForm({ ...form, budgetPct: e.target.value })} className="input" /></div>
          </div>
          <p className="text-xs text-slate-400">Proposals auto-suggest hikes from each employee's latest appraisal rating (5→12%, 4→9%, 3→6%, 2→3%, else 5%). You can fine-tune each before approving.</p>
        </form>
      </Modal>
    </div>
  );
}
